import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client';
import { loadDevMessages, loadErrorMessages } from '@apollo/client/dev';
import { CONFIG } from '@frameio/config';
import { isServer } from '@frameio/utils/config';
import { resolvers } from '../graphql/resolvers';
import { initCache } from './cache';
import { getLinks } from './links/all';
import type { ApolloClientOpts } from './types';

export function initApolloClientForClientComponent(opts: ApolloClientOpts) {
  /**
   * Stub the apollo client for the server render of client components in the
   * app directory. Most of the hooks will not invoke their calls. However,
   * subscriptions do for some reason and, if given the normal Apollo client,
   * will waste page load time traversing our normal Links.
   */
  if (isServer) {
    return new ApolloClient({
      ssrMode: false,
      link: ApolloLink.empty(), // stubs subscriptions calls
      cache: new InMemoryCache(),
      defaultOptions: {
        query: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'all',
        },
        watchQuery: {
          fetchPolicy: 'no-cache',
          errorPolicy: 'ignore',
        },
      },
    });
  }

  /**
   * Normal Apollo initialization on the client side
   */
  const link = ApolloLink.from(getLinks(opts));

  if (
    typeof window !== 'undefined' &&
    !(window as any).__APOLLO_DEVTOOLS_GLOBAL_HOOK__
  ) {
    (window as any).__APOLLO_DEVTOOLS_GLOBAL_HOOK__ = true;
  }

  if (CONFIG.thirdParty.apolloClient?.connectToDevTools) {
    loadDevMessages();
    loadErrorMessages();
  }

  if (opts.initialCacheSize) {
    (globalThis as any)[Symbol.for('apollo.cacheSize')] = opts.initialCacheSize;
  }

  return new ApolloClient({
    ssrMode: false,
    link,
    cache: initCache(),
    connectToDevTools: CONFIG.thirdParty.apolloClient?.connectToDevTools,
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: true,
      },
    },
    resolvers: resolvers as any,
    name: CONFIG.thirdParty.apolloClient?.name,
    version: CONFIG.clientIdentifierVersion,
  });
}
